import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, filter, finalize, tap } from 'rxjs/operators';
import { CustomValidators } from 'src/app/shared/validators/custom-validators';
import { FormValidationComponent } from 'src/app/shared/validators/form-validation-component';
import { Subscription, throwError } from 'rxjs';
import { AccountPublicV2Service } from '../../services/account-public.service';
import {
  PlanConsultationsResponse,
  PlanPublicV2Service,
} from 'src/app/services/plan-public-v2.service';
import {
  CPF_CNPJ_COMBINED_MASK,
  CPF_CNPJ_SPECIAL_CHARACTERS,
  CPF_MASK,
  CPF_SPECIAL_CHARACTERS,
  isLikelyCpf,
  normalizeDocumentForSubmit,
  stripDocumentMask,
} from 'src/app/_shared/utils/document-mask.util';

@Component({
  selector: 'app-user',
  templateUrl: './account-register.component.html',
  styleUrls: ['./account-register.component.scss'],
})
export class AccountRegisterComponent
  extends FormValidationComponent
  implements OnInit, OnDestroy
{
  accountId: number;
  isLoadingRegister = false;
  private intendedPlanId: number | null = null;


  form: FormGroup;
  formLabels: Record<string, string> = {
    document: 'CPF',
    cpf: 'CPF',
    cnpj: 'CNPJ',
    name: 'Nome',
    mobile: 'Celular',
    userCpf: 'CPF do usuário',
    userEmail: 'E-mail do usuário',
    password: 'Senha',
    passwordConfirm: 'Confirmação de senha',
    term: 'Termos de uso e política de privacidade',
  };

  cpfMask = CPF_MASK;
  cpfCharset = CPF_SPECIAL_CHARACTERS;
  documentMask = CPF_CNPJ_COMBINED_MASK;
  documentCharset = CPF_CNPJ_SPECIAL_CHARACTERS;

  utmTerm: string;

  planCondition!: PlanConsultationsResponse | null;
  isLoadingPlanCondition = true;

  hidePassword = true;
  hidePasswordConfirm = true;

  private subs = new Subscription();

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly router: Router,
    private readonly fb: FormBuilder,
    private readonly accountPublicV2Service: AccountPublicV2Service,
    private readonly planPublicV2Service: PlanPublicV2Service
  ) {
    super();
  }

  ngOnInit(): void {
    this.form = this.fb.group({
      document: [null, []],
      mobile: [null, []],
      name: [null, [Validators.required]],
      userCpf: [null, [Validators.required, Validators.minLength(11)]],
      userEmail: [null, [Validators.required, Validators.email]],
      password: [null, [Validators.required, CustomValidators.password()]],
      passwordConfirm: [
        null,
        [Validators.required, CustomValidators.confirmation('password')],
      ],
      email: [null, [Validators.required, Validators.email]],
      term: [null, [CustomValidators.requiredCheckbox()]],
    });

    this.listenQueryParams();
    this.documentValueChange();
    this.cnpjValueChange();
    this.listenPasswordStatusChanges();
  }

  validateForm(): void {
    Object.keys(this.form.controls).forEach((key) => {
      const control = this.form.get(key);
      control?.markAsTouched();
      control?.markAsDirty();
      control?.updateValueAndValidity();
    });

    this.form.markAsTouched();
    this.form.markAsDirty();
    this.form.updateValueAndValidity();
  }

  private documentValueChange() {
    this.subs.add(
      this.form.get('document')?.valueChanges.subscribe(() => {
        this.updateDocumentValidation();
      })
    );
  }

  private cnpjValueChange() {
    this.subs.add(
      this.form.get('cnpj')?.valueChanges.subscribe(() => {
        this.updateDocumentValidation();
      })
    );
  }

  private updateDocumentValidation() {
    const documentControl = this.form.get('document');

    const isCpf = isLikelyCpf(stripDocumentMask(documentControl.value));

    if (isCpf) {
      documentControl.setValidators([
        Validators.required,
        Validators.minLength(11),
      ]);
      this.formLabels.document = 'CPF';
    } else {
      documentControl.setValidators([
        Validators.required,
        Validators.minLength(14),
      ]);
      this.formLabels.document = 'CNPJ';
    }

    documentControl.updateValueAndValidity({ emitEvent: false });
  }

  private listenPasswordStatusChanges() {
    this.form
      .get('password')
      .statusChanges.pipe(
        filter((status) => status === 'VALID'),
        tap(() => {
          this.form.get('passwordConfirm').updateValueAndValidity();
        })
      )
      .subscribe();
  }

  onRegister() {
    this.isLoadingRegister = true;
    this.form.disable();
    const rawValue = this.form.getRawValue();
    const document = normalizeDocumentForSubmit(rawValue.document);
    const isCpf = isLikelyCpf(document);

    this.accountPublicV2Service
      .register({
        ...rawValue,
        cpf: isCpf ? document : null,
        cnpj: !isCpf ? document : null,
        intendedPlanId: this.intendedPlanId ?? undefined,
      })
      .pipe(
        catchError((error) => {
          this.isLoadingRegister = false;
          this.form.enable();
          return throwError(() => error);
        })
      )
            .subscribe((accountId) => {
        this.accountId = accountId;
        this.navigateToDigitalManagerOffer(
          rawValue.name,
          rawValue.email,
          rawValue.mobile,
          document
        );
      });
  }

  private navigateToDigitalManagerOffer(
    name: string,
    email: string,
    mobile: string,
    document: string
  ) {
    if (!this.planCondition.digitalManagerOffer.link) {
      return;
    }

    const url = new URL(this.planCondition.digitalManagerOffer.link);

    const params: Record<string, string | number | null | undefined> = {
      name,
      email,
      phone: mobile,
      doc: document,
      utm_term: this.utmTerm,
      utm_content: this.accountId,
    };

    // seta somente valores "preenchidos"
    for (const [key, value] of Object.entries(params)) {
      const param = value != null ? String(value).trim() : '';
      if (param) url.searchParams.set(key, param);
    }

    window.location.href = url.toString();
  }

  private listenQueryParams() {
    this.activatedRoute.queryParamMap.subscribe((params) => {
      this.utmTerm = params.get('utm_term');
      const planIdEncoded = params.get('aWQK') ?? btoa('5');
      const planId = Number(atob(planIdEncoded));

      this.intendedPlanId = Number.isFinite(planId) ? planId : 0;

      this.isLoadingPlanCondition = true;

      this.loadPlanConditionByPlanId(this.intendedPlanId);
    });
  }

  private loadPlanConditionByPlanId(planId) {
    this.planPublicV2Service
      .getConditionsByPlanId(planId)
      .pipe(
        finalize(() => {
          this.isLoadingPlanCondition = false;
        }),
        catchError((error) => {
          this.planCondition = null;
          return throwError(() => error);
        })
      )
      .subscribe((planCondition) => {
        this.planCondition = planCondition;
      });
  }

  copyCpfFromDocument(): void {
    const clean = stripDocumentMask(this.form.get('document')?.value);
    if (/^\d{11}$/.test(clean)) {
      this.form.patchValue({ userCpf: clean });
      this.form.get('userCpf')?.markAsDirty();
      this.form.get('userCpf')?.updateValueAndValidity();
    }
  }

  copyEmailFromAccount(): void {
    const email = this.form.get('email')?.value;
    if (email) {
      this.form.patchValue({ userEmail: email });
      this.form.get('userEmail')?.markAsDirty();
      this.form.get('userEmail')?.updateValueAndValidity();
    }
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }
}
